import * as z from 'zod';

/** Login request body — `POST /auth/merchant` (`{phone, password}`). */
export const loginCredentialsSchema = z.object({
  phone: z.string().min(1),
  password: z.string().min(1),
});
export type LoginCredentials = z.infer<typeof loginCredentialsSchema>;

/**
 * Login response — `{ access_token, refresh_token }`, nested under `data` (the
 * api layer unwraps before parsing). The endpoint is undocumented in
 * `openapi.json` (see the merchant-panel CLAUDE.md §8 — "no auth endpoint in
 * this spec"), so this stays loose on anything beyond the two tokens.
 *
 * Both tokens are persisted (`setAuthToken` / `setRefreshToken`, see
 * `use-sign-in.ts`) — the refresh token lets `@noor/api`'s http client
 * silently renew the access token on a 401 instead of bouncing to `/login`.
 */
export const loginResponseSchema = z
  .object({
    access_token: z.string().min(1),
    refresh_token: z.string().min(1),
  })
  .loose();
export type LoginResponse = z.infer<typeof loginResponseSchema>;

/**
 * OTP request body — `POST /api/v1/otp/send`.
 *
 * `app` selects which contour the code is minted for; the password-reset flow
 * is merchant-side, so it is always `"merchant"` here (the spec's own
 * ResetMerchantPassword/SetMerchantPassword notes require exactly that value).
 * `language` lets the backend localize the SMS, and is fed the UI's active
 * locale rather than a constant.
 *
 * `hash` is the Android SMS Retriever app hash. It is meaningless for a web
 * client — nothing on this origin can consume a retriever broadcast — so it is
 * omitted rather than sent empty.
 */
/**
 * Constant for OTP confirmation purpose for set password.
 */
export const OTP_PURPOSE_SET_PASSWORD = 'OTP_PURPOSE_SET_PASSWORD';

export const sendOtpRequestSchema = z.object({
  phone: z.string().min(1),
  app: z.literal('merchant'),
  language: z.string().min(1).optional(),
});
export type SendOtpRequest = z.infer<typeof sendOtpRequestSchema>;

/**
 * Confirm OTP request body — `POST /api/v1/otp/confirm`.
 */
export const confirmOtpRequestSchema = z.object({
  phone: z.string().min(1),
  otp: z.string().min(1),
  purpose: z.string().min(1),
});
export type ConfirmOtpRequest = z.infer<typeof confirmOtpRequestSchema>;

/**
 * Confirm OTP response payload — `{ confirmation_id, finish_time }`.
 */
export const confirmOtpResponseSchema = z.object({
  confirmation_id: z.string().min(1),
  finish_time: z.string().optional(),
});
export type ConfirmOtpResponse = z.infer<typeof confirmOtpResponseSchema>;

/**
 * Set-password body — `POST /api/v1/auth/merchants/set-password`.
 */
export const setMerchantPasswordSchema = z.object({
  confirmation_id: z.string().min(1),
  new_password: z.string().min(1),
});
export type SetMerchantPasswordRequest = z.infer<typeof setMerchantPasswordSchema>;

/**
 * Change-password body — `POST /api/v1/auth/change-password`.
 *
 * The one password endpoint that is AUTHENTICATED rather than public: the
 * merchant is signed in and proves themselves with the old password instead of
 * an SMS code, so there is no `phone` and no `otp` here. The http client
 * attaches the access token, and the backend reads the identity from it — which
 * is why the body names no user either.
 *
 * `new_password` follows the same 8–128 / letter+digit contract the spec states
 * for `set-password`; the schema in `features/change-password` enforces it
 * client-side so a rejectable password never reaches the network.
 */
export const changePasswordSchema = z.object({
  old_password: z.string().min(1),
  new_password: z.string().min(1),
});
export type ChangePasswordRequest = z.infer<typeof changePasswordSchema>;
